import { useEffect, useState } from 'preact/hooks';
import { useLineAssets } from './useLineAssets';
import { useTimetable } from './useTimetable';
import type { LineAssets } from '../arcticapi/types';
import type { TimetableTable } from './useTimetable';
import type { TimetableDetailsLine } from './timetableDetailsLine';
import {
  buildTimetableHref,
  getDateFromHref,
  getDirectionFromHref,
  normalizeDirection,
} from '../components/features/timetables/href';

export interface TimetableDetailsState {
  timetableLoading: boolean;
  timetableError: Error | null;
  unavailableMessage: string | null;
  timetable: TimetableTable | null;
  lineAssets: LineAssets | null;
  selectedDirection: 'outbound' | 'inbound';
  setSelectedDirection: (value: 'outbound' | 'inbound') => void;
  selectedDate: string;
  setSelectedDate: (value: string) => void;
  updateTimetable: (options?: {
    direction?: 'outbound' | 'inbound';
    date?: string;
  }) => void;
}

/**
 * Details-data controller for a selected line timetable.
 *
 * This hook keeps the selected direction and date in sync with the currently
 * requested timetable href, then fetches the timetable table and any available
 * line assets. It accepts a smaller details-line model than the search results
 * page uses, so callers can provide partial line data and let unavailable
 * assets/map links degrade naturally.
 */
export function useTimetableDetails(
  selectedLine: TimetableDetailsLine | null,
): TimetableDetailsState {
  const [selectedDirection, setSelectedDirection] = useState<
    'outbound' | 'inbound'
  >('outbound');
  const [selectedDate, setSelectedDate] = useState(getDateFromHref(null));
  const [requestedTimetableHref, setRequestedTimetableHref] = useState<
    string | null
  >(null);

  const {
    loading: timetableLoading,
    error: timetableError,
    timetable,
    unavailableMessage,
  } = useTimetable(selectedLine, requestedTimetableHref);
  const { assets: lineAssets } = useLineAssets(selectedLine, selectedDate);

  useEffect(() => {
    setSelectedDate(getDateFromHref(selectedLine?.timetableHref ?? null));
    setRequestedTimetableHref(selectedLine?.timetableHref ?? null);
    setSelectedDirection(
      getDirectionFromHref(selectedLine?.timetableHref ?? null),
    );
  }, [selectedLine?.id, selectedLine?.timetableHref]);

  useEffect(() => {
    const nextDirection = normalizeDirection(
      timetable?.direction?.name ?? null,
    );
    if (nextDirection) {
      setSelectedDirection(nextDirection);
    }
  }, [timetable?.direction?.name]);

  const updateTimetable = (options?: {
    direction?: 'outbound' | 'inbound';
    date?: string;
  }) => {
    if (!selectedLine) {
      return;
    }

    const nextDirection = options?.direction ?? selectedDirection;
    const nextDate = options?.date ?? selectedDate;

    const templateHref =
      timetable?.switchLink ??
      requestedTimetableHref ??
      selectedLine.timetableHref;

    if (!templateHref) {
      return;
    }

    if (nextDirection !== selectedDirection) {
      setSelectedDirection(nextDirection);
    }

    if (nextDate !== selectedDate) {
      setSelectedDate(nextDate);
    }

    setRequestedTimetableHref(
      buildTimetableHref(templateHref, {
        direction: nextDirection,
        date: nextDate,
      }),
    );
  };

  return {
    timetableLoading,
    timetableError,
    unavailableMessage,
    timetable,
    lineAssets,
    selectedDirection,
    setSelectedDirection,
    selectedDate,
    setSelectedDate,
    updateTimetable,
  };
}
